/**
 * The wiring.
 *
 * Everything below is plumbing: sign in, build the app, load what the chosen
 * project needs, and hand the screens a `data`/`actions` pair. All of the
 * behaviour worth arguing about lives in tested modules — this file exists so
 * that none of them has to know about React state or the browser.
 */

import { StrictMode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import './styles.css';
import { PublicClientApplication } from '@azure/msal-browser';
import { AUTH, LISTS, SITE } from './config/lists.js';
import { InteractionRequired, Session } from './auth/msal.js';
import { photoHeadroom, requestPersistence, storageFacts, type StorageFacts } from './db/persistence.js';
import { assess } from './domain/readiness.js';
import { ReadinessView } from './ui/Readiness.js';
import { LIST, type ProjectRef } from './api/sp.js';
import { createApp, type App } from './app/composition.js';
import { loadRegister, loadScript, loadShotProfiles, type Loaded, type Register, type Script } from './data/load.js';
import { CaptureStore } from './capture/store.js';
import { currentEnv, detect } from './platform/ios.js';
import { AppShell, type ShellActions, type ShellData } from './ui/AppShell.js';
import { UpdateBanner } from './ui/UpdateBanner.js';
import type { CapturedShot, ShotProfile, TemplateItem } from './domain/types.js';
import type { Scope } from './domain/capture.js';
import type { ResultInput } from './domain/verdict.js';
import type { Picked } from './capture/camera.js';

const msal = new PublicClientApplication({
  auth: { clientId: AUTH.clientId, authority: AUTH.authority, redirectUri: AUTH.redirectUri },
  cache: { cacheLocation: 'localStorage' },
});
const session = new Session({ msal: msal as never, sharePointScopes: AUTH.sharePointScopes });
const EVIDENCE_PATH = `${SITE.serverRelativeUrl}/${LISTS.evidence.title}`;

function Root() {
  const [app, setApp] = useState<App | null>(null);
  const [who, setWho] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [projects, setProjects] = useState<Loaded<ProjectRef[]> | null>(null);
  const [project, setProject] = useState<ProjectRef | null>(null);
  const [register, setRegister] = useState<Loaded<Register> | null>(null);
  const [script, setScript] = useState<Loaded<Script> | null>(null);
  const [profiles, setProfiles] = useState<readonly ShotProfile[]>([]);

  const [online, setOnline] = useState(() => navigator.onLine !== false);
  // A build waiting to take over. The worker is registered with
  // `registerType: 'prompt'` so it never swaps mid-capture — which means
  // something has to do the prompting, or a deployed fix is invisible for ever.
  const [updateReady, setUpdateReady] = useState(false);
  const [updateDeferred, setUpdateDeferred] = useState(false);
  const applyUpdate = useRef<(() => void) | null>(null);

  useEffect(() => {
    applyUpdate.current = registerSW({
      onNeedRefresh: () => setUpdateReady(true),
    });
  }, []);
  const [held, setHeld] = useState({ pendingPhotos: 0, bytesHeld: 0 });
  /**
   * The session has lapsed, but the app stays usable. Captures keep going into
   * IndexedDB; only the upload is blocked, and the banner says so.
   */
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [storage, setStorage] = useState<StorageFacts | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [readinessDismissed, setReadinessDismissed] = useState(false);

  /**
   * Ask the browser to keep this app's data, at launch rather than at first
   * capture. Without the promise, IndexedDB is best-effort and iOS clears a
   * non-installed web app's storage by itself — the engineer needs to know that
   * before the shift, not after it.
   */
  const checkStorage = useCallback(async () => {
    await requestPersistence();
    setStorage(await storageFacts());
  }, []);

  useEffect(() => {
    void checkStorage();
  }, [checkStorage]);
  const platform = useMemo(() => detect(currentEnv()), []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    addEventListener('online', on);
    addEventListener('offline', off);
    return () => {
      removeEventListener('online', on);
      removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const account = await session.initialize();
        setWho(account?.username ?? null);
        const built = await createApp({
          getToken: session.getToken,
          siteUrl: SITE.absoluteUrl,
          evidencePath: EVIDENCE_PATH,
        });
        setApp(built);
        if (!account) return;
        try {
          const live = await built.client.projects();
          setProjects({ data: live, freshness: 'live' });
          await built.local.rememberProjects(live);
          // A successful read means the token is good; MSAL hands back an hour
          // or so. What matters to the readiness check is whether it outlasts
          // the shift, and an SPA refresh token is capped at 24 hours.
          setTokenExpiresAt(Date.now() + 60 * 60 * 1000);
        } catch (e) {
          // A single-page app's refresh token lasts 24 hours, so a session that
          // lapses in a basement cannot renew itself. The engineer must still be
          // able to work: the register, script and shot list are already on the
          // phone, and captures queue locally. What they cannot do is upload.
          //
          // Locking them out here would be the worst outcome of the lot — a
          // shift's testing not done because the app showed a sign-in button
          // that cannot be pressed with no signal.
          if (!(e instanceof InteractionRequired)) throw e;
          setNeedsSignIn(true);
          const kept = await built.local.recallProjects<ProjectRef[]>();
          setProjects(
            kept
              ? { data: kept.value, freshness: 'stale', storedAt: kept.storedAt,
                  reason: 'signed-in session has lapsed — showing the last list this phone saw' }
              : { data: [], freshness: 'empty',
                  reason: 'signed-in session has lapsed and this phone has no saved project list' },
          );
        }
      } catch (e) {
        if (e instanceof InteractionRequired) {
          setNeedsSignIn(true);
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  /** Refreshes the counts behind the storage warning. Cheap: one indexed read. */
  const refreshHeld = useCallback(async () => {
    if (!app?.session) return;
    const pending = await app.local.photos.pending(app.session.project.id);
    setHeld({
      pendingPhotos: pending.length,
      bytesHeld: pending.reduce((n, p) => n + p.blob.size, 0),
    });
  }, [app]);

  const chooseProject = useCallback(
    (p: ProjectRef) => {
      if (!app) return;
      app.open(p);
      setProject(p);
      setRegister(null);
      setScript(null);
      setProfiles([]);
      (async () => {
        const scope = app.session!.scope;
        const cache = app.local.cacheFor(p.id);
        try {
          const reg = await loadRegister(scope, cache);
          setRegister(reg);
          const shots = await loadShotProfiles(scope, cache);
          setProfiles(shots.data);
          // The project's own template, never the library's: a copy is made at
          // bootstrap so a later library edit cannot reach a signed report.
          const templates = await scope.list<{ Id: number }>(LIST.templates, { select: ['Id'], top: 1 });
          if (templates[0]) setScript(await loadScript(scope, cache, templates[0].Id));
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
        await refreshHeld();
      })();
    },
    [app, refreshHeld],
  );

  const actions: ShellActions = useMemo(
    () => ({
      chooseProject,
      leaveProject: () => {
        app?.close();
        setProject(null);
        setRegister(null);
        setScript(null);
      },
      saveAnswer: (scope: Scope, item: TemplateItem, input: ResultInput) => {
        const s = app?.session;
        if (!s) return;
        // Queued, not sent: the write is local first and always, so a dropped
        // connection in a switchroom costs nothing.
        void s.queue.enqueue({
          kind: 'create',
          list: LIST.resultLines,
          label: item.label,
          fields: {
            TemplateItemId: item.id,
            AssetId: scope.assetId,
            ...(scope.breakerRef ? { BreakerRef: scope.breakerRef } : {}),
            ...(input.measuredValue !== undefined && input.measuredValue !== null
              ? { MeasuredValue: input.measuredValue }
              : {}),
            ...(input.selected ? { Selected: input.selected } : {}),
          },
        });
      },
      savePhoto: async (scope: Scope, profile: ShotProfile, picked: Picked): Promise<CapturedShot> => {
        const s = app?.session;
        if (!s || !project) throw new Error('no project open');
        const board = register?.data.boards.find((b) => b.id === scope.assetId);
        const assetTag = scope.breakerRef && board ? `${board.tag}/${scope.breakerRef}` : board?.tag ?? '';
        const store = new CaptureStore({ photos: app.local.photos });
        const target = { projectId: project.id, assetTag, shotCode: profile.shotCode };
        // Bytes first, then the queue entry: a queued upload with no bytes
        // behind it would retry forever against a photo that never existed.
        const stored = await store.keep(target, picked);
        await s.queue.enqueue({
          offlineSyncId: stored.offlineSyncId,
          kind: 'create',
          list: LIST.resultLines,
          label: `${assetTag} ${profile.shotCode}`,
          fields: {
            AssetTag: assetTag,
            ShotCode: profile.shotCode,
            CapturedAt: stored.capturedAt,
          },
        });
        void refreshHeld();
        return {
          assetTag,
          shotCode: profile.shotCode,
          photoUrl: stored.previewUrl,
          capturedAt: stored.capturedAt,
        };
      },
      syncNow: () => {
        const s = app?.session;
        if (!s) return;
        void (async () => {
          try {
            await s.queue.drain();
            await uploadPending();
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
          await refreshHeld();
        })();
      },
      retry: () => project && chooseProject(project),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [app, project, register, chooseProject, refreshHeld],
  );

  /** Sends the photo bytes, then marks them uploaded so the space can be reclaimed. */
  async function uploadPending() {
    const s = app?.session;
    if (!s || !app || !project) return;
    const library = s.evidence;
    for (const photo of await app.local.photos.pending(project.id)) {
      const ref = await library.upload(
        {
          projectCode: project.code,
          assetTag: photo.assetTag,
          shotCode: photo.shotCode,
          capturedAt: new Date(photo.capturedAt),
        },
        photo.blob,
      );
      await app.local.photos.markUploaded(photo.offlineSyncId, ref.url);
    }
    // Only now: bytes are freed once the server has them, never before.
    await app.local.photos.reclaim(project.id);
  }

  const data: ShellData = {
    projects,
    register,
    script,
    profiles,
    project,
    platform,
    online,
    pendingPhotos: held.pendingPhotos,
    bytesHeld: held.bytesHeld,
  };

  /**
   * How many photos this job still needs. Taken from the register's own
   * requirement rather than a guess, so the storage check is measured against
   * the real job and not a round number.
   */
  const plannedPhotos = (register?.data.boards ?? []).reduce((n, b) => {
    const ways = register?.data.breakersByBoard[b.tag] ?? [];
    return n + ways.reduce((m, w) => m + (w.requiredShotCount ?? 0), 0);
  }, 0);

  const readiness = storage
    ? assess({
        tokenExpiresAt: needsSignIn ? null : tokenExpiresAt,
        cached: { register: !!register?.data, script: !!script?.data, profiles: profiles.length > 0 },
        cachedAt: register?.storedAt ? Date.parse(register.storedAt) : null,
        storage: {
          persisted: storage.persisted,
          headroomPhotos: photoHeadroom(storage),
          supported: storage.supported,
        },
        platform,
        plannedPhotos,
        pendingUploads: held.pendingPhotos,
        online,
        now: Date.now(),
      })
    : null;

  if (!who) {
    return (
      <main className="signin">
        <h1>Cx Field Capture</h1>
        {error ? <p className="warn">{error}</p> : null}
        <button type="button" onClick={() => void session.signIn()} disabled={!app}>
          Sign in
        </button>
      </main>
    );
  }

  return (
    <>
      <UpdateBanner
        ready={updateReady && !updateDeferred}
        onReload={() => applyUpdate.current?.()}
        onDismiss={() => setUpdateDeferred(true)}
        pendingWrites={held.pendingPhotos}
      />
      {error ? (
        <p className="warn" role="alert">
          {error}
        </p>
      ) : null}
      {readiness && !readinessDismissed && readiness.verdict !== 'ok' ? (
        <ReadinessView
          readiness={readiness}
          onRecheck={() => void checkStorage()}
          onDismiss={() => setReadinessDismissed(true)}
        />
      ) : null}
      {needsSignIn ? (
        <p className="warn" role="alert" data-testid="session-lapsed">
          <strong>Sign-in has lapsed.</strong> Keep testing — everything you capture is being kept on this phone. It
          cannot upload until you sign in again, so do that before you finish.{' '}
          <button type="button" className="inline" onClick={() => void session.signIn()}>
            Sign in
          </button>
        </p>
      ) : null}
      <AppShell data={data} actions={actions} />
    </>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
